import { useState, useCallback } from "react";
import { format } from "date-fns";
import {
  useUpdateJobEta,
  useUpdateJobEngagementPid,
  useStartJobProcessing,
  useUpdateJobStatus,
  JobStatus,
  type JobResponse,
  type ErrorResponse,
} from "@taxassist/api-client";
import { useNotification } from "./useNotification";
import { downloadK1xFiles } from "../utils/filePreviewUtils";

// Temporary type extension until API client regeneration includes etaNum
interface JobResponseWithEtaNum extends JobResponse {
  etaNum?: number;
}

interface UseOpsJobActionsProps {
  job: JobResponse | null;
  onJobUpdate?: () => void;
}

export interface UseOpsJobActionsReturn {
  // Assignment state and handlers
  assignmentDialogOpen: boolean;
  handleAssignmentClick: () => void;
  handleAssignmentClose: () => void;
  handleAssignmentSuccess: () => void;
  handleAssignmentError: (error: string) => void;

  // ETA state and handlers
  isEditingEta: boolean;
  etaValue: string;
  handleEtaEditClick: () => void;
  handleEtaSave: () => void;
  handleEtaCancel: () => void;
  handleEtaValueChange: (value: string) => void;

  // Processing handler
  handleStartProcessing: () => void;

  // Status change handler
  handleStatusChange: (newStatus: JobStatus) => Promise<void>;

  // Engagement PID state and handlers
  isEditingEngagementPid: boolean;
  engagementPidValue: string;
  handleEngagementPidEditClick: () => void;
  handleEngagementPidSave: () => void;
  handleEngagementPidCancel: () => void;
  handleEngagementPidValueChange: (value: string) => void;
  isEngagementPidUpdatePending: boolean;

  // Mutation states
  isEtaUpdatePending: boolean;
  isStartProcessingPending: boolean;
}

/**
 * Custom hook for managing ops-specific job actions
 *
 * Handles:
 * - Job assignment dialog state
 * - ETA editing functionality with datetime conversion
 * - Job processing initiation
 * - Success/error notifications
 *
 * @param props - Configuration for the ops actions
 * @returns State and handlers for ops job actions
 */
export function useOpsJobActions({
  job,
  onJobUpdate,
}: UseOpsJobActionsProps): UseOpsJobActionsReturn {
  const notification = useNotification();

  // Assignment dialog state
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);

  // ETA editing state
  const [isEditingEta, setIsEditingEta] = useState(false);
  const [etaValue, setEtaValue] = useState<string>("");

  // ETA update mutation
  const etaUpdateMutation = useUpdateJobEta({
    mutation: {
      onSuccess: () => {
        notification.showSuccess("ETA updated successfully");
        setIsEditingEta(false);
        onJobUpdate?.();
      },
      onError: (error: ErrorResponse) => {
        notification.showError(error.message || "Failed to update ETA");
        setIsEditingEta(false);
      },
    },
  });

  // Engagement PID editing state
  const [isEditingEngagementPid, setIsEditingEngagementPid] = useState(false);
  const [engagementPidValue, setEngagementPidValue] = useState<string>("");

  // Engagement PID update mutation
  const engagementPidUpdateMutation = useUpdateJobEngagementPid({
    mutation: {
      onSuccess: () => {
        notification.showSuccess("Engagement ID updated successfully");
        setIsEditingEngagementPid(false);
        onJobUpdate?.();
      },
      onError: (error: ErrorResponse) => {
        notification.showError(error.message || "Failed to update Engagement ID");
        setIsEditingEngagementPid(false);
      },
    },
  });

  // Start processing mutation
  const startProcessingMutation = useStartJobProcessing({
    mutation: {
      onSuccess: async (data) => {
        notification.showSuccess("Job processing started successfully");
        onJobUpdate?.();

        // Check if downloadUrl is present in the response (Ultra Tax flow)
        if (data && data.downloadUrl) {
          // Trigger automatic download
          const link = document.createElement('a');
          link.href = data.downloadUrl;
          link.setAttribute('download', `sureprep_files_${job?.jobId}.zip`); // Optional: suggested filename
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          notification.showSuccess("Downloading SurePrep files for Ultra Tax processing...");
        }

        // Automatically download K1X files after starting processing (existing logic)
        if (job) {
          try {
            await downloadK1xFiles(job.jobId);
          } catch (error) {
            console.error("Failed to trigger K1X download:", error);
            // Don't show error to user - this is a nice-to-have automatic download
          }
        }
      },
      onError: (error: ErrorResponse) => {
        notification.showError(error.message || "Failed to start processing");
      },
    },
  });

  // Update job status mutation (for all status changes)
  const updateJobStatusMutation = useUpdateJobStatus({
    mutation: {
      onSuccess: () => {
        notification.showSuccess("Job status updated successfully");
        onJobUpdate?.();
      },
      onError: (error: ErrorResponse) => {
        notification.showError(error.message || "Failed to update job status");
      },
    },
  });

  // Assignment handlers
  const handleAssignmentClick = useCallback(() => {
    setAssignmentDialogOpen(true);
  }, []);

  const handleAssignmentClose = useCallback(() => {
    setAssignmentDialogOpen(false);
  }, []);

  const handleAssignmentSuccess = useCallback(() => {
    notification.showSuccess("Job assignment updated successfully");
    setAssignmentDialogOpen(false);
    onJobUpdate?.();
  }, [notification, onJobUpdate]);

  const handleAssignmentError = useCallback(
    (error: string) => {
      notification.showError(error);
    },
    [notification],
  );

  // ETA handlers
  const handleEtaEditClick = useCallback(() => {
    // Convert Unix timestamp to datetime-local format (local timezone)
    const jobWithEta = job as JobResponseWithEtaNum;
    if (jobWithEta?.etaNum && jobWithEta.etaNum !== Number.MAX_SAFE_INTEGER) {
      const date = new Date(jobWithEta.etaNum * 1000);
      // Format as datetime-local string (YYYY-MM-DDTHH:mm) for HTML input
      const localDateTime = format(date, "yyyy-MM-dd'T'HH:mm");
      setEtaValue(localDateTime);
    } else {
      setEtaValue("");
    }
    setIsEditingEta(true);
  }, [job]);

  const handleEtaSave = useCallback(async () => {
    if (!job || !etaValue) return;

    // Convert datetime-local value directly to Unix timestamp
    const unixTimestamp = Math.floor(new Date(etaValue).getTime() / 1000);

    etaUpdateMutation.mutate({
      jobId: job.jobId,
      data: { newEta: unixTimestamp },
    });
  }, [job, etaValue, etaUpdateMutation]);

  const handleEtaCancel = useCallback(() => {
    setIsEditingEta(false);
    setEtaValue("");
  }, []);

  const handleEtaValueChange = useCallback((value: string) => {
    setEtaValue(value);
  }, []);

  // Engagement PID handlers
  const handleEngagementPidEditClick = useCallback(() => {
    setEngagementPidValue(job?.engagementPid || "");
    setIsEditingEngagementPid(true);
  }, [job]);

  const handleEngagementPidSave = useCallback(() => {
    if (!job || !engagementPidValue.trim()) return;

    engagementPidUpdateMutation.mutate({
      jobId: job.jobId,
      data: { engagementPid: engagementPidValue.trim() },
    });
  }, [job, engagementPidValue, engagementPidUpdateMutation]);

  const handleEngagementPidCancel = useCallback(() => {
    setIsEditingEngagementPid(false);
    setEngagementPidValue("");
  }, []);

  const handleEngagementPidValueChange = useCallback((value: string) => {
    setEngagementPidValue(value);
  }, []);

  // Processing handler
  const handleStartProcessing = useCallback(() => {
    if (!job) return;

    startProcessingMutation.mutate({ jobId: job.jobId });
  }, [job, startProcessingMutation]);

  // Status change handler
  const handleStatusChange = useCallback(
    async (newStatus: JobStatus) => {
      if (!job) return;

      try {
        switch (newStatus) {
          case JobStatus.TRIAGE:
            // Update to NOT STARTED status
            await updateJobStatusMutation.mutateAsync({
              jobId: job.jobId,
              data: { newStatus: JobStatus.TRIAGE },
            });
            break;

          case JobStatus.PROCESSING:
          case JobStatus.DOWNLOAD_TO_PROCESS:
            // Use the start processing endpoint (which handles status transition + Ultra Tax downloads)
            await startProcessingMutation.mutateAsync({ jobId: job.jobId });
            break;

          case JobStatus.VERIFIED:
            // Update to VERIFIED status (ready for verification)
            await updateJobStatusMutation.mutateAsync({
              jobId: job.jobId,
              data: { newStatus: JobStatus.VERIFIED },
            });
            break;

          case JobStatus.DONE:
            // Update to DONE status
            await updateJobStatusMutation.mutateAsync({
              jobId: job.jobId,
              data: { newStatus: JobStatus.DONE },
            });
            break;

          default:
            notification.showError(
              `Status change to ${newStatus} is not supported`,
            );
        }
      } catch (error) {
        // Error already handled by mutation onError callbacks
        throw error;
      }
    },
    [
      job,
      startProcessingMutation,
      updateJobStatusMutation,
      notification,
    ],
  );

  return {
    // Assignment
    assignmentDialogOpen,
    handleAssignmentClick,
    handleAssignmentClose,
    handleAssignmentSuccess,
    handleAssignmentError,

    // ETA
    isEditingEta,
    etaValue,
    handleEtaEditClick,
    handleEtaSave,
    handleEtaCancel,
    handleEtaValueChange,

    // Processing
    handleStartProcessing,

    // Status change
    handleStatusChange,

    // Engagement PID
    isEditingEngagementPid,
    engagementPidValue,
    handleEngagementPidEditClick,
    handleEngagementPidSave,
    handleEngagementPidCancel,
    handleEngagementPidValueChange,
    isEngagementPidUpdatePending: engagementPidUpdateMutation.isPending,

    // Mutation states
    isEtaUpdatePending: etaUpdateMutation.isPending,
    isStartProcessingPending: startProcessingMutation.isPending,
  };
}
